/**
 * Campus parking — typed model over the vendored OSM geometry
 * (`public/geo/campus-parking.geojson`).
 *
 * Honest scope: this is MAPPED GEOMETRY only — where the lots, structures,
 * stalls and entrances are. OpenStreetMap rarely records `capacity` and
 * never live occupancy, so we surface counts of mapped features, not
 * "spaces free now". Live availability would come later from CV on the
 * CCTV feeds that cover a lot, or a CU parking-guidance API.
 *
 * Data © OpenStreetMap contributors, ODbL 1.0.
 */

import type { Feature, FeatureCollection, Geometry, Position } from "geojson";

export type ParkingKind = "surface" | "structure" | "stall" | "entrance";

export interface ParkingProps {
  kind: ParkingKind;
  name: string | null;
  nameTh: string | null;
  access: string | null;
  fee: string | null;
  /** Only present when OSM actually recorded it — never fabricated. */
  capacity: number | null;
  operator: string | null;
  osmId: string;
  subtype?: string;
}

export type ParkingFeature = Feature<Geometry, ParkingProps>;
export type ParkingCollection = FeatureCollection<Geometry, ParkingProps>;

export interface ParkingSummary {
  /** Surface lots (open-air parking areas). */
  surfaceLots: number;
  /** Multi-storey / underground / rooftop parking buildings. */
  structures: number;
  /** Individually-drawn parking bays. */
  mappedStalls: number;
  /** Vehicle entrances/exits. */
  entrances: number;
  /** Sum of `capacity` tags where OSM has them (partial — most are unmapped). */
  taggedCapacity: number;
  /** How many lots/structures carry a capacity tag (transparency on coverage). */
  withCapacityTag: number;
  /** Named CU facilities (ลานจอดรถ N / อาคารจอดรถ N etc.). */
  namedFacilities: string[];
}

const isLotOrStructure = (k: ParkingKind): boolean => k === "surface" || k === "structure";

/** Aggregate a parking collection into headline counts. Pure function. */
export function parkingSummary(collection: ParkingCollection | null | undefined): ParkingSummary {
  const empty: ParkingSummary = {
    surfaceLots: 0, structures: 0, mappedStalls: 0, entrances: 0,
    taggedCapacity: 0, withCapacityTag: 0, namedFacilities: [],
  };
  if (!collection?.features?.length) return empty;

  const names = new Set<string>();
  let surfaceLots = 0, structures = 0, mappedStalls = 0, entrances = 0;
  let taggedCapacity = 0, withCapacityTag = 0;

  for (const f of collection.features) {
    const p = f.properties;
    switch (p.kind) {
      case "surface": surfaceLots++; break;
      case "structure": structures++; break;
      case "stall": mappedStalls++; break;
      case "entrance": entrances++; break;
    }
    if (isLotOrStructure(p.kind)) {
      if (typeof p.capacity === "number" && p.capacity > 0) {
        taggedCapacity += p.capacity;
        withCapacityTag++;
      }
      // CU-official naming: ลานจอดรถ (surface lot) / อาคารจอดรถ (parking building)
      if (p.name && p.name.includes("จอดรถ")) names.add(p.name);
    }
  }

  return {
    surfaceLots, structures, mappedStalls, entrances,
    taggedCapacity, withCapacityTag,
    namedFacilities: Array.from(names).sort(),
  };
}

// ─── Planar geometry (equirectangular approximation) ────────────────────────
// Shared by the parking panel (lot footprint areas) and the building-density
// map layer (GFA proxy). At campus scale (< 3 km extent, lat ~13.7°) the
// equirectangular shoelace is accurate to well under 1% — fine for ranking
// and choropleth binning, and honest as long as we label results "computed".

/** Metres per degree of latitude (WGS-84 mean radius). */
const METERS_PER_DEG_LAT = 111_195;
const DEG_TO_RAD = Math.PI / 180;

/** Shoelace area of one linear ring of [lng, lat] positions, in m². */
export function ringAreaM2(ring: Position[]): number {
  if (!ring || ring.length < 4) return 0;
  const lat0 = ring[0][1];
  const kx = METERS_PER_DEG_LAT * Math.cos(lat0 * DEG_TO_RAD);
  const ky = METERS_PER_DEG_LAT;
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [ax, ay] = ring[i];
    const [bx, by] = ring[i + 1];
    sum += (ax * kx) * (by * ky) - (bx * kx) * (ay * ky);
  }
  return Math.abs(sum) / 2;
}

/** Footprint area in m² for Polygon / MultiPolygon (outer ring − holes). */
export function polygonAreaM2(geometry: Geometry | null | undefined): number {
  if (!geometry) return 0;
  if (geometry.type === "Polygon") {
    const [outer, ...holes] = geometry.coordinates;
    return Math.max(0, ringAreaM2(outer) - holes.reduce((s, h) => s + ringAreaM2(h), 0));
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.reduce((s, poly) => {
      const [outer, ...holes] = poly;
      return s + Math.max(0, ringAreaM2(outer) - holes.reduce((x, h) => x + ringAreaM2(h), 0));
    }, 0);
  }
  return 0;
}

// ─── Largest named facilities ────────────────────────────────────────────────

export interface NamedLot {
  name: string;
  kind: ParkingKind;
  /** Computed from the real OSM footprint (equirectangular shoelace). */
  areaM2: number;
  /** OSM capacity tag when present — never fabricated. */
  capacity: number | null;
}

/** Named lots/structures ranked by real footprint area, biggest first. */
export function largestNamedLots(
  collection: ParkingCollection | null | undefined,
  limit = 5,
): NamedLot[] {
  if (!collection?.features?.length) return [];
  const lots: NamedLot[] = [];
  for (const f of collection.features) {
    const p = f.properties;
    if (!isLotOrStructure(p.kind) || !p.name) continue;
    const areaM2 = polygonAreaM2(f.geometry);
    if (areaM2 <= 0) continue;
    lots.push({ name: p.name, kind: p.kind, areaM2, capacity: p.capacity });
  }
  return lots.sort((a, b) => b.areaM2 - a.areaM2).slice(0, limit);
}

// ─── Modeled occupancy (MODEL — no sensor feed) ──────────────────────────────
// Double-Gaussian commute curve: two arrival/afternoon humps over a night
// base, capped so weekdays plateau ~85% from 10:00–15:00 and sit ~30%
// overnight. This is a MODEL — the campus has no parking sensor feed yet.
// Callers must label output as modeled (scenario tier), never as measured.

const OCC_BASE_PCT = 30;          // overnight resting occupancy
const OCC_PEAK_CAP_PCT = 85;      // weekday midday plateau
const OCC_HUMP_AMP_PCT = 60;      // amplitude of each commute hump
const OCC_MORNING = { centerH: 10.5, sigmaH: 2.2 };
const OCC_AFTERNOON = { centerH: 14.5, sigmaH: 2.6 };
const OCC_WEEKEND_DAMPING = 0.45; // weekends run well below the commute peak

function gaussian(hour: number, centerH: number, sigmaH: number): number {
  const t = (hour - centerH) / sigmaH;
  return Math.exp(-t * t);
}

/** Modeled occupancy % at a fractional hour-of-day. Pure. */
export function occupancyPctAt(hour: number, isWeekend: boolean): number {
  const damping = isWeekend ? OCC_WEEKEND_DAMPING : 1;
  const humps =
    OCC_HUMP_AMP_PCT * gaussian(hour, OCC_MORNING.centerH, OCC_MORNING.sigmaH) +
    OCC_HUMP_AMP_PCT * gaussian(hour, OCC_AFTERNOON.centerH, OCC_AFTERNOON.sigmaH);
  return Math.round(Math.min(OCC_PEAK_CAP_PCT, OCC_BASE_PCT + damping * humps));
}

export interface ParkingOccupancyModel {
  /** 24 hourly points, 00:00 → 23:00, percent 0–100. */
  curve: number[];
  /** Modeled occupancy right now (fractional hour). */
  currentPct: number;
  /** Fractional hour-of-day used for `currentPct`. */
  hour: number;
  isWeekend: boolean;
}

/** Hour-of-day occupancy MODEL for the given moment. Deterministic per Date. */
export function modeledParkingOccupancy(now: Date = new Date()): ParkingOccupancyModel {
  const day = now.getDay();
  const isWeekend = day === 0 || day === 6;
  const hour = now.getHours() + now.getMinutes() / 60;
  const curve = Array.from({ length: 24 }, (_, h) => occupancyPctAt(h, isWeekend));
  return { curve, currentPct: occupancyPctAt(hour, isWeekend), hour, isWeekend };
}
